import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { tap, switchMap, filter, take, finalize } from 'rxjs/operators';
import { AutenticacaoService } from '../services/autenticacao.service';
import { globals } from '../globals';
import { NotificacaoService } from '../services/notificacao.service';
import { CarregandoService } from '../services/carregando.service';
import { AccessToken } from '../models/access-token';

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
  private refreshTokenInProgress = false;
  private refreshTokenSubject: BehaviorSubject<AccessToken> = new BehaviorSubject<AccessToken>(null);

  // URLS que não necessitam de autenticação (não será utilizado o access_token).
  private bypassAuth: string[] = [
    // Arquivos locais
    'assets/config/config.json',
    // Autenticação
    'account/login',
    'account/logout',
    'account/refresh-token',
    'Autenticacao/EsqueceuSenha',
    'Autenticacao/ResetarSenha',
    // Identidade visual
    'IdentidadeVisual',
    // Cadastro de contato
    'ArquivoContrato/ExisteArquivoContrato',
    'ArquivoContrato/GerarContrato',
    'Contato/Salvar',
    'ContatoAnexo/Salvar',
    'Documento/Lista',
    'Empresa/CadIntFisico',
    'Empresa/InscricaoEstadualObg',
    'Empresa/MostrarVenContato',
    'Empresa/NomeContratoParceria',
    'Empresa/UsarDocuSign',
    'Funcionario/ListaParaContatos',
    'Ibge/ListaUF',
    'Premissas/preencherTelefoneEmailConsultaCnpj',
    'Cep/BuscarCep',
    // Rastreio externo
    'Rastreio/ValidaLink',
    // Termos de aceite
    'account/termos-aceite/\\d+/arquivo',
    'account/termos-aceite/\\d+/aceitar'
  ];

  private optionalAuth: string[] = ['util/consultas-externas/cnpj/consultar'];

  constructor(
    private authenticationService: AutenticacaoService,
    private notificacaoService: NotificacaoService,
    private carregandoService: CarregandoService
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // console.log("URL: ", request.url);

    if (this.requestNoBypass(request)) {
      // Se a URL não requer autenticação (bypass), não intercepta.
      // console.log("URL no bypass: ", request.url);
      return next.handle(request);
    }

    // Busca o access token
    const accessToken = this.authenticationService.obterToken();

    if (accessToken) {
      // Se tem access token, verificar se pode utilizá-lo.

      // Verificando a validade do access token.
      const requestDate = new Date();
      const expirationDate = new Date(accessToken.expirationDate);
      const diferencaSegundos = this.retornaDiferencaDeSegundos(expirationDate, requestDate);
      const accessTokenValido = diferencaSegundos > 0;

      // console.log("Data/Hora da requisição: ", requestDate);
      // console.log("Data/Hora de validade do token: ", expirationDate);
      // console.log("Diferença em segundos: ", diferencaSegundos);
      // console.log("Token válido: ", accessTokenValido);

      if (accessTokenValido) {
        // Se o access token for válido, adiciona o header Authorization com o token.
        request = request.clone({
          setHeaders: {
            Authorization: `Bearer ${accessToken.accessToken}`
          }
        });

        return next.handle(request);
      }
    } else if (this.requestNoOpcional(request)) {
      // Se a URL estiver na lista de autenticação opcional (optional), não intercepta.
      // console.log("URL no optional: ", request.url);
      return next.handle(request);
    }

    // Caso não tenha access token válido, tentar realizar o refresh-token usando o cookie.

    if (this.refreshTokenInProgress) {
      // Coloca as requisições no buffer enquanto o refresh-token está em andamento.
      return this.refreshTokenSubject.pipe(
        filter((result) => result !== null),
        take(1),
        switchMap((data) => {
          // console.log("Passou pelo switchMap do refreshTokenSubject");
          request = request.clone({
            setHeaders: {
              Authorization: `Bearer ${data.accessToken}`
            }
          });

          return next.handle(request);
        })
      );
    } else {
      this.refreshTokenInProgress = true;
      this.refreshTokenSubject.next(null);
      this.carregandoService.mostra('renovandoToken');

      return this.authenticationService.renovarAcesso().pipe(
        tap(
          (data) => {
            // console.log("Token renovado com sucesso:", data);
            this.authenticationService.salvarToken(data);
          },
          (erro: any) => {
            // console.log("Erro ao renovar token:", erro);
            this.notificacaoService.erro(erro);
            this.authenticationService.logoutLocal();

            return next.handle(request);
          }
        ),
        switchMap((data) => {
          // console.log("Passou pelo switchMap do renovarAcesso");
          this.refreshTokenSubject.next(data);

          request = request.clone({
            setHeaders: {
              Authorization: `Bearer ${data.accessToken}`
            }
          });

          return next.handle(request);
        }),
        finalize(() => {
          this.refreshTokenInProgress = false;
          this.carregandoService.esconde('renovandoToken');
        })
      );
    }
  }

  requestNoBypass(request: HttpRequest<any>): boolean {
    const url = request.url.toLowerCase().trim();

    // Se não houver url da api, não intercepta.
    if (globals.apiUrl == null) {
      return true;
    }

    const apiUrl = globals.apiUrl.toLowerCase().trim();

    // Se a url não for da api, não intercepta.
    if (!url.startsWith(apiUrl)) {
      return true;
    }

    // Se a url estiver na lista de bypass, não intercepta.
    return this.bypassAuth.some((x) => {
      const pattern = x.toLowerCase().trim();

      // Suporte a regex: se o padrão contém caracteres especiais de regex, tenta o match
      if (pattern.includes('.*') || pattern.includes('\\d')) {
        const regex = new RegExp(`${pattern}$`);

        return regex.test(url);
      }

      return url.endsWith(pattern);
    });
  }

  requestNoOpcional(request: HttpRequest<any>): boolean {
    const url = request.url.toLowerCase().trim();

    // Se a url estiver na lista de opctional, não intercepta.
    return this.optionalAuth.some((x) => {
      const pattern = x.toLowerCase().trim();

      // Suporte a regex: se o padrão contém caracteres especiais de regex, tenta o match
      if (pattern.includes('.*') || pattern.includes('\\d')) {
        const regex = new RegExp(`${pattern}$`);

        return regex.test(url);
      }

      return url.endsWith(pattern);
    });
  }

  retornaDiferencaDeSegundos(dt2: Date, dt1: Date): number {
    const diff = (dt2.getTime() - dt1.getTime()) / 1000;

    return Math.round(diff);
  }
}
